<script>
/**
 * Overview
 * @version 1.0.0
 * @since 2.3.0
 */

import { mapActions, mapGetters, mapState } from 'vuex';
import EzNav from '@/components/ui/Nav';
import Header from '@/views/platform/venue/orders/Header.vue';
import EzBannerButton from '@/components/ui/Button/EzBannerButton.vue';
import { getContextId } from '@/util/utils';
import { LOADING_KEY } from '@/util/constants';
import EzForm from '@/components/ui/Form';
import flash from '@/components/ui/FlashMessage';
import EzLoader from '@/components/ui/Loader';
import { EzMultipleImageUpload } from '@/components/ui/ImageUpload';
import EzButton from '@/components/ui/Button';

export default {
  data() {
    return {
      stats: {},
      uploadActive: false,
      imageData: null,
      fileType: '',
      LOADING_KEY,
      hasInvoice: false,
    };
  },
  components: {
    EzNav,
    Header,
    EzBannerButton,
    EzButton,
    EzForm,
    EzLoader,
    EzMultipleImageUpload,
  },
  computed: {
    ...mapState('entities/users', ['context']),
    ...mapGetters('loading', ['getLoading']),
    venueId() {
      return getContextId();
    },
    formAction() {
      return `venues/${this.venueId}/pending-invoices`;
    },
    isUploadLoading() {
      return this.getLoading(LOADING_KEY.LOG_OFFLINE_ORDER);
    },
  },
  methods: {
    ...mapActions('entities/venues', ['venueStartStocktake']),
    updateStats(stats) {
      this.stats = stats;
    },
    reset() {
      this.imageData = null;
      this.$refs.imageUpload.reset();
      this.uploadActive = false;
      this.fileType = '';
    },
    goToFeaturePage() {
      this.$router.push({ name: 'venue-features-list' });
    },
    goToOrdering() {
      this.$router.push({ name: 'venue-orders-products' });
    },
    goToProductsWithoutGroups() {
      this.$router.push({
        name: 'venue-all-suppliers-products',
        query: { productGroup: 'unassigned' },
      });
    },
    mobileUploadInvoice(type) {
      this.fileType = type;
      this.uploadActive = true;
    },
    submitMobileInvoice() {
      this.$refs.uploadInvoiceFormMobile.onSubmit();
    },
    async imagesUploadedStep(formData) {
      this.hasInvoice = formData.get('invoices');
      this.imageData = formData;
    },
    async mobileConductStocktake() {
      const { data } = await this.venueStartStocktake({ venueId: this.venueId });
      this.$router.push({
        name: 'venue-conduct-stocktake',
        params: { id: data.data.id },
      });
    },
    mobileInventoryTransfer() {
      this.$router.push({
        name: 'venue-inventory-transfer',
      });
    },
    confirmSuccess() {
      this.reset();
      flash.success({
        title: 'Invoice successfully uploaded',
      });
    },
  },
};
</script>

<template>
  <div>
    <div class="venue-overview desktop-screen">
      <Header
        noCount
        :amount="stats.totalAmount"
        :to-show-invoices="!$permission.isFree"
        totalIcon="money-bill-wave"
        invoicesIcon="file-invoice-dollar"
        invoicesIconVariant="blue"
        invoicesLabel="Invoice pages remaining"
        :invoicesNumber="stats.pendingInvoicesRemaining"
        showLabel
        totalIconVariant="green"
        :totalIconTooltip="{}"
      >
        <template #title>Overview</template>
        <template #total>Total invoiced this month</template>
      </Header>
      <ez-nav>
        <router-view @updateStats="updateStats" />
      </ez-nav>
    </div>
    <div class="mobile-screen width-100 mobile-screen__premium">
      <div v-if="!uploadActive" class="content">
        <p class="mb-16 heading">Overview</p>
        <p class="subheading mb-32">Select which action you’d like to take.</p>
        <ez-banner-button
          class="mb-16"
          color="blue"
          icon="upload"
          @click="mobileUploadInvoice('invoice')"
          >Upload Invoice Page</ez-banner-button
        >
        <ez-banner-button
          class="mb-16"
          color="green"
          icon="list-ul"
          @click="goToOrdering"
          >Ordering</ez-banner-button
        >
        <hr />
        <ez-banner-button
          v-if="$permission.has('conductStocktake')"
          class="mb-16"
          color="orange"
          icon="clipboard-check"
          @click="mobileConductStocktake"
          >Stocktake</ez-banner-button
        >
        <ez-banner-button
          v-if="$permission.has('manageInventory')"
          class="mb-16"
          color="purple"
          icon="exchange-alt"
          @click="mobileInventoryTransfer"
          >Inventory Transfer</ez-banner-button
        >
        <ez-banner-button
          class="mb-16"
          color="yellow"
          icon="upload"
          @click="mobileUploadInvoice('credit_note')"
        >Upload Credit Note</ez-banner-button
        >
        <ez-banner-button
          class="mb-16 products-without-groups"
          color="grey"
          icon="coffee"
          @click="goToProductsWithoutGroups"
        >
          <span class="products-without-groups__label">Products without groups</span>
          <span class="products-without-groups__count" v-if="stats.productsWithoutGroupCount" >{{
            stats.productsWithoutGroupCount
          }}</span>
        </ez-banner-button>
        <hr />

        <ez-button type="blue-link" size="small" class="mb-16" @click="goToFeaturePage"
          >Feature Availability by Mobile, Web & Apps</ez-button
        >
      </div>
      <div v-else-if="uploadActive" class="content">
        <p class="mb-16 heading">
          {{ `Upload ${fileType !== 'credit_note' ? 'Invoice' : 'Credit Note'}` }}
        </p>
        <p class="subheading mb-32">
          {{
            `Have an ${
              fileType !== 'credit_note' ? 'Invoice' : 'Credit Note'
            } without an Order? Upload it here.`
          }}
        </p>
        <ez-form
          ref="uploadInvoiceFormMobile"
          method="post"
          submit-type="multipart"
          form-key="invoiceUploadMobile"
          @success="confirmSuccess"
          :action="formAction"
          :additional-data="imageData"
          :additional-headers="{ loadingKey: LOADING_KEY.LOG_OFFLINE_ORDER }"
        >
          <ez-multiple-image-upload
            ref="imageUpload"
            name="invoices"
            accept="image/jpeg,image/png,application/pdf"
            class="upload-component"
            form-key="invoiceUploadMobile"
            card-type
            @change="imagesUploadedStep"
            :number-of-files="20"
          >
            <template #cta>{{
              `Add ${fileType !== 'credit_note' ? 'Invoices' : 'Credit Notes'} `
            }}</template>
          </ez-multiple-image-upload>
          <input v-if="fileType !== ''" type="hidden" name="type" :value="fileType" />
          <ez-loader :show="isUploadLoading">Loading...</ez-loader>
          <ez-button
            class="width-100"
            form-type="submit"
            @click="submitMobileInvoice"
            :disabled="!hasInvoice"
            :is-loading="isUploadLoading"
          >
            Upload
          </ez-button>
        </ez-form>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.venue-overview {
  @include platform-tab-content();
}

.venue-overview :deep() .order-header {
  .order-header__stat:not(.order-header__stat--invoices) {
    flex-direction: column-reverse;

    small {
      text-transform: initial;
    }
  }

  .order-header__group {
    display: flex;
    flex-direction: row-reverse;
    .order-header__stats:first-child:not(:last-child) {
      padding-left: 16px;
      border-left: 1px solid #eceef5;
    }
    .order-header__stats + .order-header__stats {
      padding-right: 16px;
    }
  }
}
:deep() .navigation .navigation__nav {
  display: none;
}

.mobile-screen {
  display: none;
}

@media (max-width: 768px) {
  .mobile-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    text-align: center;

    hr {
      width: 100%;
    }

    :deep(.banner-button.products-without-groups) {
      .products-without-groups__label {
        font-weight: 500;
        flex: 1;
        text-align: left;
      }

      .products-without-groups__count {
        margin-left: auto;
        padding: 2px 10px;
        border-radius: 999px;
        background-color: $color-gray-E9;
        color: $color-gray-25;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;
      }

      .arrow {
        margin-left: 12px;
      }
    }

    &__logo {
      height: 40px;
    }

    &__title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 600;
      letter-spacing: 0.15px;
      span {
        color: $color-primary-blue;
      }
    }

    &__subtitle {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      line-height: 1.5;
      font-weight: 500;
    }

    &__desc {
      font-size: 12px;
      line-height: 1.5;
      color: $color-gray-6C;
    }

    &__download-apps {
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        height: 32px;
      }
    }

    :deep() .ez-form {
      width: 100%;
    }

    .upload-component {
      :deep() .preview__button label {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $color-primary-blue;
        color: white;
        font-size: 16px;
        line-height: 1.5;
        padding: 12px 16px;
        font-weight: 500;
        height: auto;
        svg {
          display: none;
        }
      }

      :deep() .preview__images {
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;
      }
    }
    &__premium {
      padding: 0;
      letter-spacing: 0.15px;

      .mobile-header {
        padding: 16px 24px;
        border-bottom: 1px solid #eceef5;

        display: flex;
        align-items: center;
        justify-content: space-between;

        :deep() .button-dropdown__dropdown {
          max-height: 250px;
          text-align: left;
        }

        :deep() .button-dropdown {
          .button-dropdown__toggle-container {
            .button {
              padding: 0;
            }
            .trigger {
              display: none;
            }
            .entity-info__text {
              display: none;
            }
          }
        }
      }
      .content {
        width: 100%;

        .upload-component {
          :deep() .preview__button label {
            display: flex;
            margin-bottom: 16px;
            color: $color-gray-6C;
            background-color: #f5f6fa;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            line-height: 1.5;
            padding: 12px 16px;
            font-weight: 500;
            height: auto;
            svg {
              display: block;
            }
          }
        }

        .heading {
          font-size: 20px;
          line-height: 1.4;
          font-weight: 600;
        }

        .subheading {
          font-size: 14px;
          line-height: 1.4;
          font-weight: 400;
          color: $color-gray-6C;
        }
      }
    }
  }
  .desktop-screen {
    display: none;
  }
}
</style>
